import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, Phone, MapPin } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_manifest-dinesh/artifacts/nq7jg7dl_dm%20logo%20copy%5B1%5D.png"
                alt="Dr. Dinesh Mungath"
                className="w-11 h-11 rounded-full object-cover"
              />
              <div>
                <p className="font-serif text-xl font-semibold">Manifest</p>
                <p className="text-xs tracking-[0.15em] uppercase text-slate-400">with Dr. Dinesh Mungath</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Helping you unlock wealth, health, and success through subconscious reprogramming and manifestation mastery.
            </p>
            <div className="flex gap-3">
              <a href="https://wa.me/917511107924" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-emerald-600 transition-colors" data-testid="footer-whatsapp" title="WhatsApp">
                <Phone className="w-4 h-4" />
              </a>
              <a href="mailto:Info@dineshmungath.com" className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-indigo-600 transition-colors" data-testid="footer-email" title="Email">
                <Mail className="w-4 h-4" />
              </a>
              <a href="https://www.youtube.com/@dinesh.mungath" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-red-600 transition-colors" data-testid="footer-youtube-1" title="YouTube">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://www.youtube.com/@dineshmungath" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-red-600 transition-colors" data-testid="footer-youtube-2" title="YouTube Channel 2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { label: 'About', href: '/about' },
                { label: 'Programs', href: '/courses' },
                { label: 'Services', href: '/services' },
                { label: 'Free Resources', href: '/resources' },
                { label: 'Contact', href: '/contact' },
              ].map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-slate-400 text-sm hover:text-white transition-colors" data-testid={`footer-link-${link.label.toLowerCase().replace(' ', '-')}`}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-6">Programs</h4>
            <ul className="space-y-3">
              {[
                'Money Energy Reset',
                'Subconscious Reprogramming',
                'Money Manifestation',
                'Emotional Healing',
              ].map((item) => (
                <li key={item}>
                  <Link to="/courses" className="text-slate-400 text-sm hover:text-white transition-colors">
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-6">Get in Touch</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">Info@dineshmungath.com</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                <div className="text-slate-400 text-sm">
                  <p>India: +91 7510792454</p>
                  <p className="mt-1">UAE: +97155 7472392</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">India &bull; UAE</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-xs">
            &copy; {currentYear} Manifest with Dr. Dinesh Mungath. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-slate-500 text-xs hover:text-slate-300 transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="text-slate-500 text-xs hover:text-slate-300 transition-colors" data-testid="footer-terms">Terms & Conditions</Link>
            <Link to="/refund" className="text-slate-500 text-xs hover:text-slate-300 transition-colors" data-testid="footer-refund">Refund Policy</Link>
          </div>
          <p className="text-slate-500 text-xs flex items-center gap-1">
            Made with <Heart className="w-3 h-3 text-amber-500 fill-amber-500" /> for transformation
          </p>
        </div>
      </div>
    </footer>
  );
}
